import { prefersReducedMotion } from './motion-policy.js';

const DURATION_MS = 5000;
const EXIT_MS = 180;

const CHECK_ICON = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>';
const CLOSE_ICON = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';

/**
 * The one transient confirmation surface. Any source may feed it (the server flash bag below, an
 * in-page mutation); it never takes focus. A future action (Undo) goes between the message and
 * the close button.
 */
export function showToast(
    message,
    documentRef = globalThis.document,
    windowRef = globalThis.window,
    timerApi = globalThis,
) {
    const region = documentRef?.querySelector?.('[data-toast-region]');
    const text = String(message ?? '').trim();
    if (!(region instanceof HTMLElement) || text === '') {
        return null;
    }

    const toast = documentRef.createElement('div');
    toast.className = 'toast';
    const icon = documentRef.createElement('span');
    icon.className = 'toast-icon';
    icon.innerHTML = CHECK_ICON;
    const body = documentRef.createElement('p');
    body.className = 'toast-message';
    body.textContent = text;
    const close = documentRef.createElement('button');
    close.type = 'button';
    close.className = 'toast-close';
    close.setAttribute('aria-label', 'Dismiss notification');
    close.innerHTML = CLOSE_ICON;
    toast.append(icon, body, close);

    let remainingMs = DURATION_MS;
    let startedAt = 0;
    let timerId = null;
    let hovered = false;
    let focused = false;
    let dismissed = false;
    let returnFocusTo = null;

    const pause = () => {
        if (timerId === null) {
            return;
        }

        timerApi.clearTimeout(timerId);
        timerId = null;
        remainingMs -= Date.now() - startedAt;
    };

    const resume = () => {
        if (dismissed || timerId !== null || hovered || focused || documentRef.hidden === true) {
            return;
        }

        startedAt = Date.now();
        timerId = timerApi.setTimeout(dismiss, Math.max(remainingMs, 0));
    };

    const onVisibilityChange = () => (documentRef.hidden === true ? pause() : resume());

    function dismiss() {
        if (dismissed) {
            return;
        }

        dismissed = true;
        timerApi.clearTimeout(timerId);
        timerId = null;
        documentRef.removeEventListener('visibilitychange', onVisibilityChange);
        // A keyboard user who closed it goes back to where they were, not to <body>.
        if (toast.contains(documentRef.activeElement) && returnFocusTo?.isConnected) {
            returnFocusTo.focus();
        }

        if (prefersReducedMotion(windowRef)) {
            toast.remove();
            return;
        }

        toast.dataset.state = 'dismissing';
        timerApi.setTimeout(() => toast.remove(), EXIT_MS);
    }

    toast.addEventListener('mouseenter', () => {
        hovered = true;
        pause();
    });
    toast.addEventListener('mouseleave', () => {
        hovered = false;
        resume();
    });
    toast.addEventListener('focusin', (event) => {
        if (!toast.contains(event.relatedTarget)) {
            returnFocusTo = event.relatedTarget;
        }
        focused = true;
        pause();
    });
    toast.addEventListener('focusout', (event) => {
        if (toast.contains(event.relatedTarget)) {
            return;
        }

        focused = false;
        resume();
    });
    toast.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            // Handled here: a page-level Escape (an open editor) must not also close.
            event.stopPropagation();
            dismiss();
        }
    });
    close.addEventListener('click', dismiss);
    documentRef.addEventListener('visibilitychange', onVisibilityChange);

    region.append(toast);
    resume();

    return toast;
}

export function initFlashToasts(documentRef = globalThis.document, windowRef = globalThis.window, timerApi = globalThis) {
    for (const flash of documentRef?.querySelectorAll?.('[data-toast-flash]') ?? []) {
        showToast(flash.content?.textContent ?? flash.textContent, documentRef, windowRef, timerApi);
        flash.remove();
    }
}
